import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * The page's heraldic furniture.
 *
 * Every piece here is structural: it marks hierarchy (section openings, the
 * banner's edge, the map frame) rather than decorating. The brief bans
 * skeuomorphic medieval kitsch and the design system bans textures, gradient
 * surfaces and emoji-as-icons, so the period reading has to come from shape and
 * flat brand colour — quartered shields, pennant hems, a neatline — never from
 * faux parchment or blackletter.
 */

/** Quartered shield, gules and or. The page's repeating device. */
export const Shield = ({ className }: { className?: string }) => (
    <svg
        viewBox="0 0 14 18"
        className={cn('h-[22px] w-[18px] flex-none', className)}
        aria-hidden="true"
    >
        {/*
         * The clip path is inlined per instance rather than shared through one
         * <defs> id: this device renders many times per page, and a duplicated id
         * resolves to whichever copy the document happens to hit first.
         */}
        <clipPath id="rf-shield-clip">
            <path d="M1 1 h12 v10 l-6 6 -6-6 z" />
        </clipPath>
        <g clipPath="url(#rf-shield-clip)">
            <rect x="0" y="0" width="7" height="9" fill="var(--rf-gules)" />
            <rect x="7" y="0" width="7" height="9" fill="var(--rf-gold)" />
            <rect x="0" y="9" width="7" height="9" fill="var(--rf-gold)" />
            <rect x="7" y="9" width="7" height="9" fill="var(--rf-gules)" />
        </g>
        <path
            d="M1 1 h12 v10 l-6 6 -6-6 z"
            fill="none"
            stroke="var(--rf-blu-950)"
            strokeWidth="1.1"
        />
    </svg>
)

/**
 * Section heading: shield, title, then a rule out to the margin (the rule is
 * the `.rf-h2::after` pseudo-element in globals.css).
 */
export const SectionHeading = ({
    children,
    className,
}: {
    children: ReactNode
    className?: string
}) => (
    <h2
        className={cn(
            'rf-h2 m-0 flex items-center gap-[10px] text-2xl leading-[1.2] font-bold',
            className,
        )}
    >
        <Shield />
        {children}
    </h2>
)

/**
 * Bunting hung off the banner's bottom edge, so the dark band reads as the cord
 * the flags are strung from. Adjacent flags touch at the top — that adjacency is
 * what reads as bunting rather than as a zigzag cut into the band.
 */
export const PennantHem = () => (
    <div aria-hidden="true" className="block leading-[0]">
        <svg width="100%" height="30" preserveAspectRatio="none" className="block">
            <defs>
                <pattern id="rf-pennants" width="88" height="30" patternUnits="userSpaceOnUse">
                    <path d="M0 0 h44 l-22 28 z" fill="var(--rf-gules)" />
                    <path d="M44 0 h44 l-22 28 z" fill="var(--rf-gold)" />
                </pattern>
            </defs>
            <rect width="100%" height="30" fill="url(#rf-pennants)" />
        </svg>
    </div>
)

/**
 * The footer's hem: the bunting's shape in the footer's own ink, so the page
 * closes the loop without a second red-and-gold strip competing with the top.
 */
export const FooterHem = () => (
    <div aria-hidden="true" className="block leading-[0] text-[var(--rf-smoke-950)]">
        <svg width="100%" height="26" preserveAspectRatio="none" className="block">
            <defs>
                <pattern id="rf-foot-hem" width="88" height="26" patternUnits="userSpaceOnUse">
                    <path d="M0 26 h44 l-22 -24 z" fill="currentColor" />
                    <path d="M44 26 h44 l-22 -24 z" fill="currentColor" />
                </pattern>
            </defs>
            <rect width="100%" height="26" fill="url(#rf-foot-hem)" />
        </svg>
    </div>
)

/**
 * Compass rose for the map frame — hairline, low opacity. Hidden on small
 * screens rather than crowding a 340px-tall map.
 */
export const CompassRose = () => (
    <svg
        viewBox="0 0 56 56"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        aria-hidden="true"
        className="pointer-events-none absolute top-4 right-4 z-[500] hidden h-14 w-14 text-[var(--rf-blu-950)] opacity-40 sm:block"
    >
        <circle cx="28" cy="28" r="21.5" />
        <circle cx="28" cy="28" r="15" />
        <path d="M28 4.5 L31 24 L28 28 L25 24 Z" fill="currentColor" stroke="none" />
        <path d="M28 51.5 L25 32 L28 28 L31 32 Z" />
        <path d="M51.5 28 L32 31 L28 28 L32 25 Z" />
        <path d="M4.5 28 L24 25 L28 28 L24 31 Z" />
        <path
            d="M44.6 11.4 L33 23 M11.4 44.6 L23 33 M44.6 44.6 L33 33 M11.4 11.4 L23 23"
            strokeWidth="0.75"
        />
        <text
            x="28"
            y="12"
            textAnchor="middle"
            fontSize="7"
            fontWeight="700"
            fill="currentColor"
            stroke="none"
        >
            N
        </text>
    </svg>
)
